/**
 * SessionsPage (Doc 52) — User self-service session management.
 *
 * - List active sessions
 * - Mark current device
 * - Revoke individual session (logout that device)
 */
import {
  Loader2,
  Monitor,
  RefreshCw,
  Smartphone,
  Trash2,
} from "lucide-react";
import * as React from "react";

import { mySessionsApi, type SessionItem } from "@/api/sessions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useUIStore } from "@/stores/ui";

function fmtDate(s: string): string {
  return new Date(s).toLocaleString("vi-VN");
}

function deviceLabel(ua: string | null): string {
  if (!ua) return "Unknown device";
  const lower = ua.toLowerCase();
  if (lower.includes("iphone")) return "iPhone";
  if (lower.includes("ipad")) return "iPad";
  if (lower.includes("android")) return "Android";
  if (lower.includes("mobile")) return "Mobile";
  if (lower.includes("mac")) return "Mac";
  if (lower.includes("windows")) return "Windows";
  if (lower.includes("linux")) return "Linux";
  return "Desktop";
}

function browserLabel(ua: string | null): string | null {
  if (!ua) return null;
  const lower = ua.toLowerCase();
  if (lower.includes("edg/")) return "Edge";
  if (lower.includes("chrome/") && !lower.includes("edg/")) return "Chrome";
  if (lower.includes("firefox/")) return "Firefox";
  if (lower.includes("safari/") && !lower.includes("chrome")) return "Safari";
  return null;
}

export function SessionsPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [items, setItems] = React.useState<SessionItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [revoking, setRevoking] = React.useState<string | null>(null);
  const [includeRevoked, setIncludeRevoked] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await mySessionsApi.list(includeRevoked);
      setItems(r.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được sessions",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [includeRevoked, pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleRevoke = async (s: SessionItem): Promise<void> => {
    if (s.is_current) {
      const ok = window.confirm(
        "Đây là session hiện tại — revoke sẽ logout bạn ngay. Tiếp tục?",
      );
      if (!ok) return;
    }
    setRevoking(s.id);
    try {
      await mySessionsApi.revoke(s.id);
      pushToast({
        variant: "success",
        title: "Đã revoke session",
        description: s.is_current
          ? "Đăng xuất khỏi trang này..."
          : "Device đó sẽ phải đăng nhập lại.",
      });
      if (s.is_current) {
        // Force reload — refresh token revoked
        window.location.href = "/#/";
        return;
      }
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Revoke thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Phiên đăng nhập</h1>
          <p className="text-sm text-muted-foreground">
            Quản lý các thiết bị đang đăng nhập tài khoản của bạn. Revoke
            session sẽ logout device đó. Doc 52.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
            />
            Hiển thị đã revoke
          </label>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            <RefreshCw className="size-4 mr-1.5" /> Reload
          </Button>
        </div>
      </header>

      {loading ? (
        <Card>
          <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Đang tải…
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Không có session nào.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const dev = deviceLabel(s.user_agent);
            const isMobile =
              dev === "iPhone" || dev === "iPad" || dev === "Android" || dev === "Mobile";
            const Icon = isMobile ? Smartphone : Monitor;
            return (
              <Card
                key={s.id}
                className={
                  s.is_current
                    ? "border-primary"
                    : !s.is_active
                      ? "opacity-60"
                      : ""
                }
              >
                <CardContent className="p-4 flex items-start gap-3">
                  <Icon className="size-5 text-muted-foreground mt-1" />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">
                        {dev}
                        {browserLabel(s.user_agent)
                          ? ` · ${browserLabel(s.user_agent)}`
                          : ""}
                      </span>
                      {s.is_current ? (
                        <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded font-bold">
                          ĐANG DÙNG
                        </span>
                      ) : null}
                      {!s.is_active ? (
                        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                          {s.revoked_at ? "REVOKED" : "EXPIRED"}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      IP: {s.ip_address ?? "?"} · Login: {fmtDate(s.created_at)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Expires: {fmtDate(s.expires_at)}
                      {s.revoked_at
                        ? ` · Revoked: ${fmtDate(s.revoked_at)}`
                        : ""}
                    </div>
                    {s.user_agent ? (
                      <div className="text-[10px] text-muted-foreground font-mono truncate">
                        {s.user_agent}
                      </div>
                    ) : null}
                  </div>
                  {s.is_active ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={revoking === s.id}
                      onClick={() => void handleRevoke(s)}
                    >
                      {revoking === s.id ? (
                        <Loader2 className="size-3 animate-spin mr-1" />
                      ) : (
                        <Trash2 className="size-3 mr-1" />
                      )}
                      Revoke
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
